import React, { useState } from "react";
import { Languages } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { LangMenu } from "@/components/common/LangMenu";
import { CentralClock } from "@/components/common/CentralClock";
import { ModSidebar } from "@/components/mod/ModSidebar";
import { ModOverview } from "@/components/mod/ModOverview";
import { ModReports } from "@/components/mod/ModReports";
import { ModAgents } from "@/components/mod/ModAgents";
import { ModRanks } from "@/components/mod/ModRanks";
import { ModPages } from "@/components/mod/ModPages";
import { ModSla } from "@/components/mod/ModSla";
import { ModStatuses } from "@/components/mod/ModStatuses";
import { ModGuidelines } from "@/components/mod/ModGuidelines";
import { ModAchievements } from "@/components/mod/ModAchievements";
import { ModIpBans } from "@/components/mod/ModIpBans";
import { ModNotices } from "@/components/mod/ModNotices";
import { ModLiveChats } from "@/components/mod/ModLiveChats";
import { ModSchedule } from "@/components/mod/ModSchedule";
import { ModForwarding } from "@/components/mod/ModForwarding";
import { ModLogs } from "@/components/mod/ModLogs";
import { ModAcademy } from "@/components/mod/ModAcademy";
import { ModTeam } from "@/components/mod/ModTeam";
import { ModDisputes } from "@/components/mod/ModDisputes";
import { ModDataRequest } from "@/components/mod/ModDataRequest";
import { ReportForm } from "@/components/report/ReportForm";
import { SuccessPanel } from "@/components/report/SuccessPanel";

export function ModDashboard() {
  const { user, has } = useAuth();
  const { tr, lang, setLang } = useLang();
  const [view, setView] = useState("overview");
  const [newResult, setNewResult] = useState(null);

  const titles = {
    overview: "Overview", reports: tr.mTabReports, new: tr.mTabNew,
    agents: tr.mTabAgents, ranks: tr.mTabRanks, pages: "Legal Pages", disputes: "Disputes", emergency: "Emergency line", sla: "SLA deadlines", team: "Csapat", data: "Adatigénylés", statuses: "Státuszok", guidelines: "Irányelvek", achievements: "Eredmények", bans: "IP tiltások", notices: "Közlemények", schedule: "Beosztás", forwarding: "Report továbbító", academy: "Reporter Academy", logs: "Tevékenységi napló",
  };

  return (
    <div className="flex h-screen w-full overflow-hidden bg-zinc-950 text-zinc-50">
      <ModSidebar view={view} setView={(v) => { setView(v); setNewResult(null); }} />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="relative flex h-14 shrink-0 items-center justify-between border-b border-zinc-800/70 px-4">
          <div className="flex items-center gap-3">
            <select
              data-testid="mod-view-select"
              value={view}
              onChange={(e) => { setView(e.target.value); setNewResult(null); }}
              className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-[13px] text-zinc-200 md:hidden"
            >
              <option value="overview">Overview</option>
              {has("MODERATE") && <option value="reports">{tr.mTabReports}</option>}
              {has("MODERATE") && <option value="disputes">Disputes</option>}
              {has("MODERATE") && <option value="emergency">Emergency line</option>}
              {has("MODERATE") && <option value="data">Adatigénylés</option>}
              {has("CREATE_REPORTS") && <option value="new">{tr.mTabNew}</option>}
              {user.role === "admin" && <option value="agents">{tr.mTabAgents}</option>}
              {user.role === "admin" && <option value="ranks">{tr.mTabRanks}</option>}
              {user.role === "admin" && <option value="sla">SLA deadlines</option>}
              {user.role === "admin" && <option value="statuses">Státuszok</option>}
              {user.role === "admin" && <option value="guidelines">Irányelvek</option>}
              {user.role === "admin" && <option value="achievements">Eredmények</option>}
              {user.role === "admin" && <option value="bans">IP tiltások</option>}
              {user.role === "admin" && <option value="notices">Közlemények</option>}
              {user.role === "admin" && <option value="schedule">Beosztás</option>}
              {user.role === "admin" && <option value="forwarding">Report továbbító</option>}
              {user.role === "admin" && <option value="academy">Akadémia</option>}
              {user.role === "admin" && <option value="logs">Napló</option>}
              {user.role === "admin" && <option value="team">Csapat</option>}
              {user.role === "admin" && <option value="pages">Legal Pages</option>}
            </select>
            <h1 className="text-[15px] font-semibold tracking-tight text-zinc-100">{titles[view]}</h1>
          </div>
          <CentralClock className="absolute left-1/2 hidden -translate-x-1/2 lg:flex" />
          <LangMenu />
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto p-5">
          {view === "overview" && <ModOverview onOpenReports={() => setView("reports")} />}
          {view === "reports" && has("MODERATE") && <ModReports />}
          {view === "disputes" && has("MODERATE") && <ModDisputes />}
          {view === "emergency" && has("MODERATE") && <ModLiveChats />}
          {view === "data" && has("MODERATE") && <ModDataRequest />}
          {view === "new" && has("CREATE_REPORTS") && (
            <div className="mx-auto max-w-3xl rounded-2xl border border-zinc-800/70 bg-[#111113] p-6 sm:p-8">
              {newResult ? (
                <SuccessPanel result={newResult} onReset={() => setNewResult(null)} />
              ) : (
                <>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-50">{tr.mCreateOnBehalf}</h2>
                  <p className="mb-6 mt-1 text-[13px] text-zinc-500">{tr.formSubtitle}</p>
                  <ReportForm apiPath="/mod/reports" onSuccess={setNewResult} />
                </>
              )}
            </div>
          )}
          {view === "agents" && user.role === "admin" && <ModAgents />}
          {view === "ranks" && user.role === "admin" && <ModRanks />}
          {view === "sla" && user.role === "admin" && <ModSla />}
          {view === "statuses" && user.role === "admin" && <ModStatuses />}
          {view === "guidelines" && user.role === "admin" && <ModGuidelines />}
          {view === "achievements" && user.role === "admin" && <ModAchievements />}
          {view === "bans" && user.role === "admin" && <ModIpBans />}
          {view === "notices" && user.role === "admin" && <ModNotices />}
          {view === "schedule" && user.role === "admin" && <ModSchedule />}
          {view === "forwarding" && user.role === "admin" && <ModForwarding />}
          {view === "academy" && user.role === "admin" && <ModAcademy />}
          {view === "logs" && user.role === "admin" && <ModLogs />}
          {view === "team" && user.role === "admin" && <ModTeam />}
          {view === "pages" && user.role === "admin" && <ModPages />}
        </main>
      </div>
    </div>
  );
}
